import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Smartphone } from "lucide-react";
import { useSiteSection } from "@/hooks/useSiteContent";
import { siteImageUrl } from "@/hooks/useSiteData";
import { PageHero, PageIntro } from "@/components/site/PageHero";
import { SimplePlans } from "@/components/site/SimplePlans";
import { Reveal } from "@/components/site/Reveal";

export const Route = createFileRoute("/aplicativos")({
  head: () => ({
    meta: [
      { title: "Aplicativos e Streaming — Contato Internet" },
      {
        name: "description",
        content:
          "Assine pacotes com Deezer, Max, Paramount+, Skeelo e mais apps de streaming direto na sua fatura da Contato Internet.",
      },
      { property: "og:title", content: "Aplicativos e Streaming — Contato Internet" },
      {
        property: "og:description",
        content: "Streaming, música e leitura inclusos na sua fatura.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: AppsPage,
});

function AppIconStrip({ logos }: { logos: { name: string; image: string }[] }) {
  if (logos.length === 0) return null;
  return (
    <div className="mt-5 w-full border-t border-border pt-4">
      <p className="text-[0.65rem] font-extrabold uppercase tracking-wide text-muted-foreground">
        Apps do catálogo
      </p>
      <div className="mt-2.5 flex flex-wrap justify-center gap-2">
        {logos.map((app) => {
          const src = siteImageUrl(app.image);
          return (
            <span
              key={app.name}
              title={app.name}
              className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-lg bg-secondary"
            >
              {src ? (
                <img src={src} alt={app.name} loading="lazy" className="size-full object-contain p-1" />
              ) : (
                <span className="text-[9px] font-bold uppercase text-primary">
                  {app.name.slice(0, 2)}
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function AppsPage() {
  const apps = useSiteSection("apps");
  const items = (apps.items ?? []).filter((i) => i.name);
  const logos = (apps.apps ?? []).filter((a) => a.name);

  const defaultItem = items.find((i) => i.badge) ?? items[0];
  const [selectedName, setSelectedName] = useState<string | undefined>(defaultItem?.name);

  return (
    <>
      <PageHero eyebrow={apps.eyebrow} title={apps.title} subtitle={apps.subtitle} />
      <PageIntro text={apps.text} />

      <section className="bg-background pb-14 sm:pb-20">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal variant="fade-up">
            <SimplePlans
              items={items}
              icon={Smartphone}
              waPrefix="Olá! Quero contratar o pacote de aplicativos"
              selectedName={selectedName}
              onSelect={(item) => setSelectedName(item.name)}
              renderExtra={(item) => (
                <AppIconStrip
                  logos={logos.filter((a) =>
                    (item.tier ?? "standard") === "premium"
                      ? a.tier === "premium"
                      : a.tier !== "premium",
                  )}
                />
              )}
            />
          </Reveal>
        </div>
      </section>
    </>
  );
}
